import React, { useState, useEffect } from 'react';
import RestaurantTimingsContainer from './container';
import { useGetVendorTimingsQuery, useUpdateVendorTimingsMutation } from './services';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

const DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday"
];

const RestaurantTimings = () => {
    const loginUserData = useSelector((state) => state?.posData);
    const vendorId = loginUserData?.vendor?.id;

    const { data: resp, isLoading, isFetching, refetch } = useGetVendorTimingsQuery();
    const isTimingsLoading = isLoading || (isFetching && !resp?.data);
    const [updateTimings, { isLoading: isUpdating }] = useUpdateVendorTimingsMutation();

    const [timings, setTimings] = useState(
        DAYS.map((_, index) => ({
            day_of_week: index === 6 ? 0 : index + 1, // Sunday is 0 in backend, Monday 1... (Wait, backend says 0=Sun, 1=Mon, ..., 6=Sat)
            is_open: true,
            slots: [{ open_time: "10:00", close_time: "23:00" }]
        }))
    );

    // Sync with fetched data
    useEffect(() => {
        if (resp?.data) {
            const fetchedTimings = resp.data;
            const newTimings = DAYS.map((_, index) => {
                // Map day_of_week: Monday=1, ..., Saturday=6, Sunday=0
                const dayNum = index === 6 ? 0 : index + 1;
                const existing = fetchedTimings.find(t => t.day_of_week === dayNum);
                if (existing) {
                    return {
                        day_of_week: dayNum,
                        is_open: existing.is_open,
                        slots: existing.slots.length > 0
                            ? existing.slots.map(s => ({
                                open_time: s.open_time.substring(0, 5),
                                close_time: s.close_time.substring(0, 5)
                            }))
                            : [{ open_time: "10:00", close_time: "22:00" }]
                    };
                }
                return {
                    day_of_week: dayNum,
                    is_open: true,
                    slots: [{ open_time: "10:00", close_time: "22:00" }]
                };
            });
            setTimings(newTimings);
        }
    }, [resp]);

    const handleToggleDay = (dayIdx) => {
        const newTimings = [...timings];
        newTimings[dayIdx].is_open = !newTimings[dayIdx].is_open;
        setTimings(newTimings);
    };

    const handleAddSlot = (dayIdx) => {
        const newTimings = [...timings];
        newTimings[dayIdx].slots.push({ open_time: "10:00", close_time: "22:00" });
        setTimings(newTimings);
    };

    const handleRemoveSlot = (dayIdx, slotIdx) => {
        const newTimings = [...timings];
        if (newTimings[dayIdx].slots.length > 1) {
            newTimings[dayIdx].slots.splice(slotIdx, 1);
            setTimings(newTimings);
        } else {
            toast.error("At least one slot is required if the day is open.");
        }
    };

    const handleUpdateSlot = (dayIdx, slotIdx, field, value) => {
        const newTimings = [...timings];
        newTimings[dayIdx].slots[slotIdx][field] = value;
        setTimings(newTimings);
    };

    const handleCopyAll = (dayIdx) => {
        const sourceSlots = [...timings[dayIdx].slots];
        const newTimings = timings.map(t => ({
            ...t,
            slots: sourceSlots.map(s => ({ ...s }))
        }));
        setTimings(newTimings);
        // toast.success("Timings copied to all days");
    };

    const checkOverlap = (slots) => {
        if (!slots || slots.length <= 1) return Array(slots?.length || 0).fill(false);
        const results = Array(slots.length).fill(false);
        for (let i = 1; i < slots.length; i++) {
            for (let j = 0; j < i; j++) {
                const sEarlier = slots[j];
                const sCurrent = slots[i];
                // Check if sEarlier and sCurrent overlap
                if (sEarlier.open_time < sCurrent.close_time && sCurrent.open_time < sEarlier.close_time) {
                    results[i] = true;
                    break;
                }
            }
        }
        return results;
    };

    const overlaps = timings.map(t => t.is_open ? checkOverlap(t.slots) : Array(t.slots.length).fill(false));

    const handleSave = async () => {
        if (overlaps.some(day => day.some(o => o === true))) {
            toast.error("Please resolve overlapping time slots before saving.");
            return;
        }

        try {
            const body = {
                // vendor_id: vendorId,
                timings: timings.map(t => ({
                    day_of_week: t.day_of_week,
                    is_open: t.is_open,
                    slots: t.slots.map(s => ({
                        open_time: s.open_time + ":00",
                        close_time: s.close_time + ":00"
                    }))
                }))
            };
            await updateTimings(body).unwrap();
            refetch();
        } catch (error) {
            toast.error(error?.data?.message || "Failed to update timings");
        }
    };

    return (
        <RestaurantTimingsContainer
            days={DAYS}
            timings={timings}
            onToggleDay={handleToggleDay}
            onAddSlot={handleAddSlot}
            onRemoveSlot={handleRemoveSlot}
            onUpdateSlot={handleUpdateSlot}
            onCopyAll={handleCopyAll}
            onSave={handleSave}
            isSaving={isUpdating}
            overlaps={overlaps}
            isLoading={isTimingsLoading}
        />
    );
};

export default RestaurantTimings;
